import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { contains, filter, map, size } from 'underscore';
import { ActionButton, Checkbox, Label } from '@fluentui/react';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { getDemographicStyle } from './style';

const UnstyledDemographic = (props) => {
  const {
    className,
    classes,
    i18n,
    title,
    description,
    formatter,
    dimensions,
    availableDimensions,
    onChangeDimensions,
    popupId,
    onValidate,
    needValidation,
  } = props;

  const [errorMessage, setErrorMessage] = useState('');

  const onToggleDimension = useCallback((dimension) => {
    if (contains(dimensions, dimension)) {
      onChangeDimensions(filter(dimensions, d => d !== dimension));
    } else {
      onChangeDimensions([...dimensions, dimension]);
    }
  }, [dimensions, onChangeDimensions]);

  const onToggleAll = useCallback(() => {
    if (size(dimensions) < size(availableDimensions)) {
      onChangeDimensions(Object.keys(availableDimensions));
    } else {
      onChangeDimensions([]);
    }
  }, [availableDimensions, dimensions, onChangeDimensions]);

  useEffect(() => {
    if (needValidation) {
      const error = dimensions.length > 0 ? '' : i18n.getString(_TL_('At least one target needs to be selected.'));
      setErrorMessage(error);
      onValidate(error);
    }
  }, [dimensions, onValidate, i18n, needValidation])

  return (
    <div className={classNames([className, classes.demographicContainer])}>
      <Label>
        {title}
        {popupId && <HelpPopup
          aria-label={title}
          topic={popupId}
        />}
      </Label>
      <div>{description || null}</div>
      <ActionButton
        className="toggle-dimensions"
        onClick={onToggleAll}
      >
        {size(dimensions) < size(availableDimensions) ? i18n.getString(_TL_('Select all')) : i18n.getString(_TL_('Clear all'))}
      </ActionButton>
      <div className="dimensions">
        {map(availableDimensions, (_, dimension) => (
          <Checkbox
            key={`demographic-${dimension}`}
            label={formatter(dimension, i18n)}
            checked={contains(dimensions, dimension)}
            onChange={() => onToggleDimension(dimension)}
          />
        ))}
      </div>
      { errorMessage && <div className="error-message">{errorMessage}</div> }
    </div>
  );
};

UnstyledDemographic.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape({
    demographicContainer: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  formatter: PropTypes.func.isRequired,
  dimensions: PropTypes.arrayOf(PropTypes.string),
  availableDimensions: PropTypes.shape({}).isRequired,
  onChangeDimensions: PropTypes.func.isRequired,
  popupId: PropTypes.string,
  onValidate: PropTypes.func,
  needValidation: PropTypes.bool,
};

UnstyledDemographic.defaultProps = {
  className: '',
  description: '',
  dimensions: [],
  popupId: '',
  onValidate: () => {},
  needValidation: false,
};

export const Demographic = React.memo(withDefaultStyles((UnstyledDemographic), getDemographicStyle));
